import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Drawer, IconButton, Box, Typography, Badge } from '@mui/material';
import { CalendarDays, MessageCircle, Tags, Users } from 'lucide-react';
import {HomeIcon, PlusIcon,GamepadIcon, ImageGalleryIcon, ChevronUpIcon} from './PixelIcons';
import { getAuthStatus } from '../utils/api';
import { useNotifications } from '../contexts/NotificationsContext';

const BottomNav = ({ onOpenAssistant }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    incomingFriendRequests,
    refreshNotifications,
    unseenRewards,
  } = useNotifications();

  const navItems = [
    { icon: PlusIcon, path: '/add-task', id: 'add-task', label: 'New Task' },
    { icon: HomeIcon, path: '/user-home', id: 'user-home', label: 'Home' },
    { icon: Users, path: '/friends', id: 'friends', label: 'Friends' },
    { icon: ImageGalleryIcon, path: '/gallery', id: 'gallery', label: 'Rewards' },
    { icon: GamepadIcon, path: '/game-modes', id: 'game-modes', label: 'Game Modes' },
    { icon: Tags, path: '/categories', id: 'categories', label: 'Categories' },
    { icon: CalendarDays, path: '/events', id: 'events', label: 'Events', activePaths: ['event', 'events'] },
    { icon: MessageCircle, path: '/assistant', id: 'assistant', label: 'Assistant' },
  ];

  const current = location.pathname.split('/').filter(Boolean)[0] || 'user-home';

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const res = await getAuthStatus();
        if (alive) {
          setIsAuthenticated(!!res?.authenticated);
          setLoading(false);
        }
      } catch (_) {
        if (alive) {
          setIsAuthenticated(false);
          setLoading(false);
        }
      }
    })();
    return () => { alive = false; };
  }, [location.pathname]);

  useEffect(() => {
    if (isAuthenticated && !loading) {
      refreshNotifications();
    }
  }, [location.pathname, isAuthenticated, loading, refreshNotifications]);

  useEffect(() => {
    if (!isAuthenticated || loading) return undefined;
    const handleRewardsUpdate = (event) => {
      if (event.detail?.quiet) return;
      refreshNotifications();
    };
    window.addEventListener('rewardsUpdate', handleRewardsUpdate);
    return () => {
      window.removeEventListener('rewardsUpdate', handleRewardsUpdate);
    };
  }, [isAuthenticated, loading, refreshNotifications]);

  if (!isAuthenticated || loading) return null;

  return (
    <Drawer
      id="app-bottom-navigation"
      variant="persistent"
      anchor="bottom"
      open={true}
      PaperProps={{
        sx: {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 999,
          borderTopRightRadius: 16,
          borderTopLeftRadius: 16,
          overflow: 'hidden',
          backgroundColor: 'primary.seeThrough',
          border: '4px solid rgba(0, 0, 0, 1)',
          paddingTop:  1,
          paddingRight:  2 ,
          paddingLeft:  2 ,
          paddingBottom: 1,
        },
      }}
    >
      <IconButton
        id="app-bottom-navigation-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1,
          padding: 0,
          color: 'secondary.main',
          '&:hover': {color:"secondary.main", backgroundColor: '#000000' },
          border: '2px solid',
          borderColor: 'secondary.main',
          backgroundColor: '#000000',
          mt:1,
        }}
      >
        <ChevronUpIcon width={30} height={30} />
      </IconButton>

      <Box
        id="app-bottom-navigation-items"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '8px',
          alignItems: 'start',
          justifyContent: 'center',
          paddingTop: isExpanded ? 2 : 1,
          paddingBottom: isExpanded ? 2 : 1,
          paddingLeft: 2,
          paddingRight: 2,
          mt:4
        }}
      >
        {(isExpanded ? navItems : navItems.slice(0, 4)).map((item, idx) => {
          const badgeContent = item.id === 'gallery'
            ? unseenRewards
            : item.id === 'friends'
              ? incomingFriendRequests
              : 0;

          return (
            <Box
              id={`app-bottom-navigation-${item.id}`}
              onClick={() => item.id === 'assistant' && onOpenAssistant ? onOpenAssistant() : navigate(item.path)}
              key={idx}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: '#000000',
                border: '2px solid',
                borderColor: 'secondary.main',
                borderRadius: 2,
                padding: 1,
                justifyContent: 'center',
                height: 70,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'green.main',
                },
              }}
            >
              {badgeContent > 0 ? (
                <Badge badgeContent={badgeContent} color="secondary">
                  <IconButton
                    sx={{
                      color: (item.activePaths || [item.id]).includes(current) ? 'green.main' : 'secondary.main',
                      padding: 0,
                      width: 30,
                      height: 30,
                      pointerEvents: 'none', // prevent it from capturing clicks
                    }}
                  >
                    <item.icon width={30} height={30} />
                  </IconButton>
                </Badge>
              ) : (
                <IconButton
                  sx={{
                    color: (item.activePaths || [item.id]).includes(current) ? 'green.main' : 'secondary.main',
                    padding: 0,
                    width: 30,
                    height: 30,
                    pointerEvents: 'none', // prevent it from capturing clicks
                  }}
                >
                  <item.icon width={30} height={30} />
                </IconButton>
              )}
              <Typography sx={{
                fontSize: '0.6rem',
                color: (item.activePaths || [item.id]).includes(current) ? 'green.main' : 'secondary.main',
                textAlign: 'center',
                marginTop: 0.5,
              }} variant='body2'>
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Drawer>
  );
};

export default BottomNav;
